import React, { useState } from 'react';
import {
  COMPANY_NAME,
  COMPANY_SHORT,
  COMPANY_LOCATION,
  COMPANY_ADDRESS_FULL,
  MAP_URL,
  ORG_TYPE,
  CHARTER_NUMBER,
  EFFECTIVE_DATE,
  FOUNDING_YEAR,
  MISSION,
  TAX_NOTICE,
  CONTACT_EMAIL,
  PROGRAMS,
  HOW_IT_WORKS,
  GET_INVOLVED,
  NAV_ITEMS,
} from './constants';

function mailto(subject) {
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

// Programmatic anchor jump — keeps the URL hash in sync, but computes the
// scroll target manually so the sticky header never covers the section
// heading. Works regardless of browser support for `scroll-margin-top`.
function jumpToAnchor(event, href) {
  if (!href || !href.startsWith('#')) return;
  if (typeof document === 'undefined') return;
  const id = href.slice(1);
  const el = document.getElementById(id);
  if (!el) return;
  if (event && typeof event.preventDefault === 'function') event.preventDefault();
  const offset = 80; // sticky header (~64) + small breathing room
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: 'smooth' });
  if (window.history && typeof window.history.replaceState === 'function') {
    window.history.replaceState(null, '', href);
  }
}

// Tiny editorial section label: small caps + leading rule.
function SectionTag({ index, label, light = false }) {
  const c = light ? 'text-camp-bone/70' : 'text-camp-ink/60';
  const ruleC = light ? 'bg-camp-bone/40' : 'bg-camp-ink/30';
  return (
    <div className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest ${c}`}>
      <span className={`h-px w-8 ${ruleC}`} aria-hidden="true" />
      <span>{index ? `${index} / ` : ''}{label}</span>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const handleNav = (e, href) => {
    jumpToAnchor(e, href);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-camp-ink/15 bg-camp-bone/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-4">
        <a href="#top" onClick={(e) => handleNav(e, '#top')} className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-bold leading-none text-camp-ink">CAMP</span>
          <span className="font-display text-2xl font-bold leading-none text-camp-coral">.</span>
          <span className="ml-2 hidden font-mono text-[10px] uppercase tracking-widest text-camp-ink/55 sm:inline">
            EST. {FOUNDING_YEAR}
          </span>
        </a>

        <nav
          aria-label="Primary"
          className="hidden items-center gap-5 font-mono text-[11px] uppercase tracking-widest text-camp-ink/70 md:flex"
        >
          {NAV_ITEMS.map((item, i) => (
            <React.Fragment key={item.href}>
              {i > 0 && <span aria-hidden="true" className="text-camp-ink/25">/</span>}
              <a
                href={item.href}
                onClick={(e) => handleNav(e, item.href)}
                className="transition hover:text-camp-coral"
              >
                {item.label}
              </a>
            </React.Fragment>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="#get-involved"
            onClick={(e) => handleNav(e, '#get-involved')}
            className="rounded-full bg-camp-coral px-5 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-white transition hover:bg-camp-coral-d"
          >
            Apply →
          </a>
          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-camp-ink/20 bg-camp-bone text-camp-ink transition hover:bg-camp-ink hover:text-camp-bone md:hidden"
          >
            <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
            <span aria-hidden="true" className="font-mono text-base leading-none">{open ? '×' : '☰'}</span>
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Mobile"
          className="border-t border-camp-ink/15 bg-camp-bone md:hidden"
        >
          <ul className="mx-auto max-w-6xl px-5 py-3">
            {NAV_ITEMS.map((item) => (
              <li key={item.href} className="border-b border-camp-ink/10 last:border-b-0">
                <a
                  href={item.href}
                  onClick={(e) => handleNav(e, item.href)}
                  className="flex items-center justify-between py-3 font-mono text-xs font-bold uppercase tracking-widest text-camp-ink hover:text-camp-coral"
                >
                  <span>{item.label}</span>
                  <span aria-hidden="true">→</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-camp-bone bg-grid-dots bg-grid-20"
    >
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-20 md:grid-cols-12 md:gap-12 md:py-28">
        <div className="md:col-span-8">
          <SectionTag index="00" label="Canton's Career Studio" />
          <h1 className="mt-6 font-display text-5xl font-bold leading-[1.05] tracking-tight text-camp-ink md:text-7xl lg:text-[88px]">
            Career pathways, <br />
            <span className="italic text-camp-coral">built by hand.</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-camp-ink/75 md:text-xl">
            CAMP Inc. pairs Early College High School students of the Canton City school system with mentors and apprenticeship hosts — turning ambition into a defined career pathway.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#programs"
              onClick={(e) => jumpToAnchor(e, '#programs')}
              className="group inline-flex items-center gap-2 rounded-full bg-camp-ink px-6 py-3 font-mono text-[12px] font-bold uppercase tracking-widest text-camp-bone transition hover:bg-camp-coral"
            >
              View Programs
              <span aria-hidden="true" className="transition group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#contact"
              onClick={(e) => jumpToAnchor(e, '#contact')}
              className="font-mono text-[12px] font-bold uppercase tracking-widest text-camp-ink underline-offset-4 hover:underline"
            >
              Get In Touch
            </a>
          </div>
        </div>

        {/* asymmetric stat rail */}
        <aside className="md:col-span-4">
          <div className="grid h-full content-end gap-3">
            <div className="border-l-2 border-camp-coral pl-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Founded</p>
              <p className="mt-1 font-display text-3xl font-bold text-camp-ink">{FOUNDING_YEAR}</p>
            </div>
            <div className="border-l-2 border-camp-ink pl-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Headquartered</p>
              <p className="mt-1 font-display text-3xl font-bold text-camp-ink">Canton, OH</p>
            </div>
            <div className="border-l-2 border-camp-marker pl-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Status</p>
              <p className="mt-1 font-display text-3xl font-bold text-camp-ink">Ohio Nonprofit</p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="border-t border-camp-ink/15 bg-camp-bone">
      <div className="mx-auto max-w-6xl px-5 py-24 md:py-32">
        <SectionTag index="01" label="Our Mission" />
        <div className="mt-10 grid gap-12 md:grid-cols-12 md:gap-16">
          <blockquote className="md:col-span-9">
            <p
              className="font-display text-3xl font-medium leading-snug text-camp-ink md:text-5xl"
              style={{ textIndent: '2.5rem' }}
            >
              <span aria-hidden="true" className="float-left -ml-1 mr-3 mt-1 font-display text-7xl font-bold leading-none text-camp-coral md:-ml-2 md:text-8xl">&ldquo;</span>
              {MISSION}
            </p>
            <footer className="mt-8 font-mono text-[11px] uppercase tracking-widest text-camp-ink/60">
              — From the Articles of Incorporation, {COMPANY_NAME}
            </footer>
          </blockquote>

          <aside className="md:col-span-3">
            <div className="border-t border-camp-ink/30 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">In short</p>
              <p className="mt-3 font-display text-xl font-medium leading-snug text-camp-ink">
                Apprenticeship + Mentorship + Support for Canton's next generation of working professionals.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function ProgramTile({ program, featured }) {
  return (
    <article
      className={`group flex flex-col border border-camp-ink p-7 transition hover:bg-camp-ink hover:text-camp-bone ${
        featured ? 'md:col-span-2 md:row-span-2 md:p-10' : ''
      } bg-camp-paper`}
    >
      <div className="flex items-start justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest opacity-60">
          {program.index} / 03
        </p>
        {featured && (
          <span className="rounded-full bg-camp-coral px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">
            Featured
          </span>
        )}
      </div>
      <h3
        className={`mt-6 font-display font-bold leading-tight ${
          featured ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl'
        }`}
      >
        {program.title}
      </h3>
      <p className={`mt-4 ${featured ? 'text-lg' : 'text-base'} leading-relaxed opacity-80`}>
        {program.summary}
      </p>
      <ul className="mt-6 space-y-2 font-mono text-[11px] uppercase tracking-wider opacity-75">
        {program.bullets.map((b) => (
          <li key={b} className="flex gap-3">
            <span aria-hidden="true">→</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function Programs() {
  // Bento: first program spans 2 cols × 2 rows on md+
  const indexed = PROGRAMS.map((p, i) => ({ ...p, index: String(i + 1).padStart(2, '0') }));
  return (
    <section id="programs" className="border-t border-camp-ink/15 bg-camp-bone">
      <div className="mx-auto max-w-6xl px-5 py-24 md:py-32">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <SectionTag index="02" label="What We Offer" />
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-bold leading-tight text-camp-ink md:text-6xl">
              Three programs, <span className="italic">one</span> career pathway.
            </h2>
          </div>
          <p className="max-w-md font-mono text-[12px] uppercase leading-relaxed tracking-wider text-camp-ink/60">
            Each student joins all three pillars — apprenticeship, mentorship, and support — designed to work as one integrated track.
          </p>
        </div>

        <div className="mt-12 grid gap-0 md:grid-cols-3 md:grid-rows-2">
          <ProgramTile program={indexed[0]} featured />
          <ProgramTile program={indexed[1]} />
          <ProgramTile program={indexed[2]} />
        </div>
      </div>
    </section>
  );
}

function WhoWeServe() {
  return (
    <section
      id="students"
      className="relative border-t border-b border-camp-ink/15 bg-camp-coral text-white"
    >
      <div className="mx-auto max-w-5xl px-5 py-24 text-center md:py-32">
        <SectionTag light label="Who We Serve" />
        <p className="mt-8 font-display text-3xl font-medium italic leading-snug md:text-5xl lg:text-6xl">
          Early College High School students of the Canton City school system —
          <br className="hidden md:block" />
          <span className="not-italic font-bold"> ambitious young people</span> ready to learn the business of a real career.
        </p>
        <div className="mt-10 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-white/85">
          <span className="h-px w-12 bg-white/60" aria-hidden="true" />
          Eligibility verified through partnership with Canton City schools
          <span className="h-px w-12 bg-white/60" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

function ProcessRow({ step, title, body, last }) {
  return (
    <div className={`grid grid-cols-12 gap-6 py-10 md:gap-12 md:py-14 ${last ? '' : 'border-b border-camp-ink/20'}`}>
      <div className="col-span-12 md:col-span-3">
        <p className="font-display text-7xl font-bold leading-none text-camp-coral md:text-8xl">{step}</p>
      </div>
      <div className="col-span-12 md:col-span-9">
        <h3 className="font-display text-3xl font-bold text-camp-ink md:text-4xl">{title}</h3>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-camp-ink/75">{body}</p>
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <section id="process" className="bg-camp-bone">
      <div className="mx-auto max-w-6xl px-5 py-24 md:py-32">
        <div className="max-w-2xl">
          <SectionTag index="03" label="The Process" />
          <h2 className="mt-6 font-display text-4xl font-bold leading-tight text-camp-ink md:text-6xl">
            Three steps from interest to career.
          </h2>
        </div>
        <div className="mt-14 border-t border-camp-ink/20">
          {HOW_IT_WORKS.map((s, i) => (
            <ProcessRow key={s.step} {...s} last={i === HOW_IT_WORKS.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function InvolvedRow({ audience, cta, body, subject, last }) {
  return (
    <div
      className={`grid grid-cols-12 items-center gap-4 py-7 md:gap-8 md:py-9 ${
        last ? '' : 'border-b border-camp-ink/20'
      }`}
    >
      <div className="col-span-12 md:col-span-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">For</p>
        <p className="mt-1 font-display text-2xl font-bold text-camp-ink md:text-3xl">{audience}</p>
      </div>
      <p className="col-span-12 text-base leading-relaxed text-camp-ink/75 md:col-span-6 md:text-lg">{body}</p>
      <div className="col-span-12 md:col-span-3 md:text-right">
        <a
          href={mailto(subject)}
          className="inline-flex items-center gap-2 rounded-full border-2 border-camp-ink bg-transparent px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-camp-ink transition hover:bg-camp-ink hover:text-camp-bone"
        >
          {cta} <span aria-hidden="true">→</span>
        </a>
      </div>
    </div>
  );
}

function GetInvolved() {
  return (
    <section id="get-involved" className="border-t border-camp-ink/15 bg-camp-marker/30">
      <div className="mx-auto max-w-6xl px-5 py-24 md:py-32">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <SectionTag index="04" label="Get Involved" />
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-bold leading-tight text-camp-ink md:text-6xl">
              Three roles, all <span className="italic">essential.</span>
            </h2>
          </div>
        </div>
        <div className="mt-12 border-t-2 border-camp-ink">
          {GET_INVOLVED.map((g, i) => (
            <InvolvedRow key={g.audience} {...g} last={i === GET_INVOLVED.length - 1} />
          ))}
        </div>
        <p className="mt-10 max-w-2xl font-mono text-[11px] uppercase tracking-wider text-camp-ink/65">
          {TAX_NOTICE}
        </p>
      </div>
    </section>
  );
}

function Credentials() {
  return (
    <section id="credentials" className="border-t border-camp-ink/15 bg-camp-bone">
      <div className="mx-auto max-w-6xl px-5 py-24 md:py-32">
        <SectionTag index="05" label="On Public Record" />
        <h2 className="mt-6 max-w-2xl font-display text-4xl font-bold leading-tight text-camp-ink md:text-6xl">
          A registered Ohio nonprofit.
        </h2>

        <div className="mt-12 grid gap-0 border border-camp-ink md:grid-cols-3">
          <div className="border-b border-camp-ink p-7 md:border-b-0 md:border-r">
            <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Entity Type</p>
            <p className="mt-3 font-display text-xl font-bold leading-snug text-camp-ink md:text-2xl">{ORG_TYPE}</p>
          </div>
          <div className="border-b border-camp-ink p-7 md:border-b-0 md:border-r">
            <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Charter Number</p>
            <p className="mt-3 font-display text-3xl font-bold text-camp-coral md:text-4xl">{CHARTER_NUMBER}</p>
          </div>
          <div className="p-7">
            <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Effective</p>
            <p className="mt-3 font-display text-xl font-bold leading-snug text-camp-ink md:text-2xl">{EFFECTIVE_DATE}</p>
          </div>
        </div>

        <p className="mt-8 max-w-2xl font-mono text-[11px] uppercase tracking-wider text-camp-ink/60">
          On file with the Ohio Secretary of State. {TAX_NOTICE}
        </p>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="border-t border-camp-ink/15 bg-camp-bone">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-24 md:grid-cols-12 md:py-32">
        <div className="md:col-span-7">
          <SectionTag index="06" label="Get In Touch" />
          <h2 className="mt-6 font-display text-5xl font-bold leading-[1.05] tracking-tight text-camp-ink md:text-7xl">
            Let&apos;s talk about <br />
            the <span className="italic text-camp-coral">next cohort.</span>
          </h2>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-camp-ink/75">
            Reach out for student applications, mentor signups, or partnership inquiries — we read every message.
          </p>
        </div>

        <div className="md:col-span-5">
          <div className="border border-camp-ink bg-camp-paper p-8">
            <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Mailing Address</p>
            <p className="mt-2 font-display text-xl font-bold text-camp-ink">{COMPANY_LOCATION}</p>
            <p className="mt-1 text-sm text-camp-ink/75">{COMPANY_ADDRESS_FULL}</p>
            <a
              href={MAP_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-3 inline-block font-mono text-[11px] font-bold uppercase tracking-widest text-camp-coral underline-offset-4 hover:underline"
            >
              View on Google Maps →
            </a>

            <div className="mt-8 border-t border-camp-ink/25 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-camp-ink/55">Email</p>
              <a
                href={mailto('Inquiry — CAMP Inc.')}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-camp-coral px-5 py-3 font-mono text-[12px] font-bold uppercase tracking-widest text-white transition hover:bg-camp-coral-d"
              >
                Send a message <span aria-hidden="true">→</span>
              </a>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-camp-ink/55">
                Opens your default email app
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-camp-ink text-camp-bone">
      <div className="mx-auto max-w-6xl px-5 py-16">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="font-display text-5xl font-bold leading-none">
              CAMP<span className="text-camp-coral">.</span>
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-camp-bone/65">
              {COMPANY_NAME} — an Ohio nonprofit serving Early College High School students of the Canton City school system.
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-camp-bone/55">Visit</p>
            <p className="mt-2 text-sm text-camp-bone/85">{COMPANY_LOCATION}</p>
            <p className="text-sm text-camp-bone/65">{COMPANY_ADDRESS_FULL}</p>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-camp-bone/55">Sections</p>
            <ul className="mt-2 space-y-1.5 text-sm">
              <li><a className="text-camp-bone/85 hover:text-camp-coral" href="#about" onClick={(e) => jumpToAnchor(e, '#about')}>About</a></li>
              <li><a className="text-camp-bone/85 hover:text-camp-coral" href="#programs" onClick={(e) => jumpToAnchor(e, '#programs')}>Programs</a></li>
              <li><a className="text-camp-bone/85 hover:text-camp-coral" href="#credentials" onClick={(e) => jumpToAnchor(e, '#credentials')}>Legal</a></li>
              <li><a className="text-camp-bone/85 hover:text-camp-coral" href="#contact" onClick={(e) => jumpToAnchor(e, '#contact')}>Contact</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-camp-bone/15 pt-6">
          <p className="max-w-3xl font-mono text-[10px] uppercase leading-relaxed tracking-wider text-camp-bone/55">
            {TAX_NOTICE}
          </p>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-camp-bone/45">
            © {new Date().getFullYear()} {COMPANY_NAME} · All rights reserved
          </p>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-camp-bone font-sans text-camp-ink">
      <a
        href="#main"
        onClick={(e) => jumpToAnchor(e, '#main')}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-camp-coral focus:px-4 focus:py-2 focus:font-mono focus:text-[11px] focus:font-bold focus:uppercase focus:tracking-widest focus:text-white"
      >
        Skip to content
      </a>
      <Header />
      <main id="main">
        <Hero />
        <About />
        <Programs />
        <WhoWeServe />
        <HowItWorks />
        <GetInvolved />
        <Credentials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
